import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { openaiLogin, type Credential, type OpenaiLoginAttempt } from '../../api/endpoints';
import { Button } from '../../components/Button';
import { Card } from '../../components/Surface';
import { ErrorBanner } from '../../components/ErrorBanner';
import { useT } from '../../i18n/provider';
import { useCompanyId } from './useCompany';

export function OpenaiConnection({ rows }: { rows: Credential[] }) {
  const t = useT();
  const companyId = useCompanyId();
  const cache = useQueryClient();
  const [attempt, setAttempt] = useState<OpenaiLoginAttempt | null>(null);
  const [target, setTarget] = useState('');
  const start = useMutation({
    mutationFn: () => openaiLogin.start(companyId, { label: 'ChatGPT', ...(target ? { credential_id: target } : {}) }),
    onSuccess: setAttempt,
  });
  const status = useQuery({
    queryKey: ['openai-login', companyId, attempt?.id],
    queryFn: () => openaiLogin.status(companyId, attempt!.id),
    enabled: attempt !== null && attempt.status === 'pending',
    refetchInterval: (query) => query.state.data?.status && query.state.data.status !== 'pending' ? false : 1500,
  });
  const current = status.data ?? attempt;
  const cancel = useMutation({ mutationFn: () => openaiLogin.cancel(companyId, attempt!.id), onSuccess: (value) => { setAttempt(value); cache.setQueryData(['openai-login', companyId, value.id], value); } });
  useEffect(() => {
    if (current?.status === 'connected') {
      void cache.invalidateQueries({ queryKey: ['credentials', companyId] });
      void cache.invalidateQueries({ queryKey: ['company', companyId] });
    }
  }, [current?.status, cache, companyId]);
  const pending = current?.status === 'pending';
  return <Card size="sm" className="space-y-3">
    <p className="font-semibold">{t('credentials.openai.title')}</p>
    <p className="text-sm text-ink-soft">{t('credentials.openai.intro')}</p>
    <label className="block text-sm">{t('credentials.openai.connection')}
      <select className="ml-2 rounded border border-line bg-raised p-2" disabled={pending} value={target} onChange={(event) => setTarget(event.target.value)}>
        <option value="">{t('credentials.openai.new')}</option>
        {rows.filter((row) => row.type === 'openai_subscription_oauth' && row.status !== 'revoked').map((row) => <option key={row.id} value={row.id}>{row.label}</option>)}
      </select>
    </label>
    {start.error || status.error || cancel.error ? <ErrorBanner error={start.error ?? status.error ?? cancel.error} /> : null}
    {current ? <p role="status">{t(`credentials.openai.states.${current.status}`)}</p> : null}
    {pending && current?.verification_url && current.user_code ? <div className="space-y-2">
      <a href={current.verification_url} target="_blank" rel="noopener noreferrer" className="text-primary underline">{t('credentials.openai.authorize')}</a>
      <p>{t('credentials.openai.code')} <strong className="select-all font-mono">{current.user_code}</strong></p>
    </div> : null}
    {pending ? <Button variant="ghost" busy={cancel.isPending} onClick={() => cancel.mutate()}>{t('common.cancel')}</Button>
      : <Button busy={start.isPending} onClick={() => start.mutate()}>{t(target ? 'credentials.openai.reconnect' : 'credentials.openai.connect')}</Button>}
  </Card>;
}
